/**
 * Shared API contract types.
 *
 * Imported by BOTH the Hono server and the React SPA, so that the shape the
 * server returns and the shape `useAuth()` exposes cannot drift apart.
 *
 * ⚠️ NO DEPENDENCIES, ever — that is the rule, and "types only" is not. This
 * package holds a handful of pure functions as well (`nameActivityShares`,
 * `foldName`, `hasAtLeastRole`), and each one is here for the same reason: two
 * or more surfaces have to agree on it exactly, and a hand-copied second
 * implementation is how they stop agreeing. What must never appear is anything
 * that reaches for a database, a network or a Node built-in, because everything
 * in this file is linked into the browser bundle.
 */

/**
 * Roles are derived live from Telegram group membership, never stored as the
 * source of truth (PRD section 5).
 *
 *   creator | administrator            -> 'admin'
 *   member | restricted+in, tagged     -> 'officer'   (DECISION-3)
 *   member | restricted+in             -> 'member'
 *   left | kicked | absent             -> no session at all
 *
 * 'officer' comes from a Telegram member TAG, so it grants no moderation
 * power in the chat itself. It is off unless TELEGRAM_OFFICER_TAG is
 * configured, because it is only safe once the group disallows members
 * editing their own tags — see DECISION-3.
 */
export type MemberRole = 'admin' | 'officer' | 'member';

/** Ordered least to most privileged, for comparisons. */
export const ROLE_RANK: Record<MemberRole, number> = { member: 0, officer: 1, admin: 2 };

export function hasAtLeastRole(role: MemberRole, required: MemberRole): boolean {
  return ROLE_RANK[role] >= ROLE_RANK[required];
}

/** The authenticated principal, as exposed to the frontend. */
export interface AuthenticatedUser {
  /** Telegram numeric user id. Stable forever; the primary key everywhere. */
  telegramUserId: number;
  role: MemberRole;
  /** Convenience mirror of `role === 'admin'`, required by PRD section 5. */
  isAdmin: boolean;
  /**
   * Telegram @username without the @, if the user has one set.
   * Stored verbatim — PRD section 7 requires Latin-script nicknames are never
   * transliterated.
   */
  username: string | null;
  firstName: string | null;
  lastName: string | null;
  photoUrl: string | null;
}

/** GET /api/me */
export interface MeResponse {
  user: AuthenticatedUser;
}

/**
 * POST /auth/miniapp — the Mini App's one exchange (DECISION-32).
 *
 * This endpoint sets NO cookie — the token is the whole credential, presented
 * as `Authorization: Bearer <token>`. On Telegram Web the Mini App is framed,
 * which is a cross-site context where a `SameSite=Lax` cookie is simply not
 * sent, so a cookie would not work there anyway; and because this is the one
 * flow whose credential can arrive from a URL hash, a cookie here would be a
 * cross-user write. See DECISION-32.
 *
 * It is the SAME token the cookie carries: same secret, same TTL, same
 * `verifySession`. There is no second session format to keep in step.
 *
 * Hold it in memory only. An httpOnly cookie beats a JS-readable token against
 * XSS, and `localStorage` is strictly worse than both; re-authentication is
 * silent (re-POST the cached initData on any 401), so persisting it buys no UX
 * and only widens exposure.
 */
export interface MiniAppAuthResponse {
  ok: true;
  token: string;
}

/**
 * GET /api/public-config — unauthenticated.
 *
 * Exists so the SPA can render the Telegram Login Widget without the bot
 * username being baked in at build time. That keeps the Docker image
 * environment-agnostic: changing bots never requires a frontend rebuild.
 */
export interface PublicConfigResponse {
  /**
   * Which identity flow to render.
   *
   * 'oidc'   — Telegram OpenID Connect. Preferred; the page just links to
   *            `oidcStartPath`.
   * 'widget' — the legacy iframe Login Widget, used only when the deployment
   *            has no OIDC credentials configured.
   *
   * Authorisation is identical either way — getChatMember decides access.
   */
  loginMode: 'oidc' | 'widget';
  /** Where to send the browser to begin the OIDC flow. */
  oidcStartPath: string;
  /** Bot @username without the @, for the widget's data-telegram-login attr. */
  botUsername: string;
  /** Absolute URL the widget should redirect to after the user approves. */
  loginCallbackUrl: string;
  /** True only in non-production builds; see DECISION-13. */
  devLoginEnabled: boolean;
}

export type ApiErrorCode =
  /** No session cookie, or it failed signature/expiry checks. */
  | 'unauthenticated'
  /** Valid session, but Telegram says the user is no longer in the group. */
  | 'not_a_member'
  /** Valid member, insufficient role for this route. */
  | 'forbidden'
  /** Login Widget / initData payload failed HMAC or freshness validation. */
  | 'invalid_telegram_payload'
  /**
   * Could not reach Telegram to verify membership. Deliberately distinct from
   * 'not_a_member': the request is denied either way, but the user is told
   * "cannot verify right now" rather than "you were removed".
   */
  | 'telegram_unavailable'
  /**
   * The action needs the bot, and no bot is running.
   *
   * Distinct from 'internal' for the same reason 'telegram_unavailable' is
   * distinct from 'not_a_member': nothing is broken and retrying will not
   * help. The fix is a configuration change and a redeploy, so the UI has to
   * be able to say that rather than offering a retry.
   */
  | 'bot_disabled'
  | 'bad_request'
  | 'internal';

export interface ApiErrorBody {
  error: {
    code: ApiErrorCode;
    message: string;
  };
}

/* -------------------------------------------------------------------------
 * Member attributes (Phase 2 surface, schema deliberately generic today)
 *
 * TODO(OPEN-ITEM-2): the concrete field list is unspecified. These types
 * describe the *catalogue* mechanism, not any particular field.
 * See docs/OPEN_ITEMS.md.
 * ---------------------------------------------------------------------- */

export type AttributeValueType =
  | 'text'
  | 'number'
  | 'boolean'
  | 'date'
  | 'single_select'
  | 'multi_select';

export interface AttributeDefinition {
  id: number;
  key: string;
  label: string;
  description: string | null;
  valueType: AttributeValueType;
  /** Allowed choices for single_select / multi_select, else null. */
  options: string[] | null;
  /** Display unit, e.g. "lvl". */
  unit: string | null;
  /**
   * Whether this attribute is expected to change over time and be charted.
   * Storage is append-only either way; this only drives UI presentation.
   */
  isTimeSeries: boolean;
  sortOrder: number;
  isActive: boolean;
}

export interface AttributeValue {
  id: number;
  memberId: number;
  defId: number;
  /** Canonical value, shape determined by the definition's valueType. */
  value: string | number | boolean | string[] | null;
  /** ISO 8601. When the value was true, not when the row was written. */
  recordedAt: string;
  recordedByUserId: number | null;
}

/**
 * GET /api/roster — the shared character list.
 *
 * `values` is keyed by the attribute definition's `key`, so a caller can read
 * `values.level` without also fetching the catalogue. Every key is optional by
 * construction: the catalogue is admin-seeded (DECISION-9) and ships empty, so
 * a missing key means "this deployment has no such attribute, or nobody has
 * filled it in" — two states the UI must render identically, because it cannot
 * tell them apart and neither is an error.
 */
export interface RosterEntry {
  /**
   * ⚠️ `memberId` IS NULLABLE (DECISION-33). An admin can create a character for
   * a player who does not use Telegram, and it has no owner — so every consumer
   * has to be able to render a row with no name, no handle and no avatar behind
   * it. Comparing it against the viewer's own id still works: `null` matches
   * nobody, which is correct.
   */
  character: { id: number; memberId: number | null; name: string };
  memberUsername: string | null;
  memberFirstName: string | null;
  memberLastName: string | null;
  values: Record<string, AttributeValue['value']>;
  /**
   * Karma received, all time (DECISION-37). Zero for everybody by default.
   *
   * On the ROSTER rather than behind its own request because the home page draws
   * a heart beside a name it is already rendering, and a second fetch to fill in
   * one number per row is the shape that makes a list flicker.
   */
  karma: number;
  /**
   * How many points the VIEWER may still give this character today.
   *
   * ⚠️ THE ONE VIEWER-SCOPED FIELD ON THIS RESPONSE, and it is here rather than
   * in its own endpoint for a reason the page can be judged on: without it the
   * heart is offered unconditionally, somebody writes a thank-you, presses send,
   * and only then learns they had nothing left to give. It is 0 on the viewer's
   * OWN row too — you cannot thank yourself — and the client tells those two
   * apart from `character.memberId`, which it already has.
   */
  karmaRemainingToday: number;
}

export interface RosterResponse {
  characters: RosterEntry[];
}

/* -------------------------------------------------------------------------
 * The loot rotation (DECISION-33)
 * ---------------------------------------------------------------------- */

/**
 * One character's place in the rotation.
 *
 * The order is STORED, not derived: `position` is what the server holds, and
 * the array arrives in that order. A client must not re-sort it — the whole
 * point of the rework is that the order is a decision somebody made, and any
 * client-side ordering rule would silently overrule it.
 */
export interface LootQueueEntry {
  /** 1-based and contiguous. Also the array index + 1; sent so it can be shown. */
  position: number;
  characterId: number;
  characterName: string;
  /** Null for an admin-created character with no Telegram owner. */
  memberId: number | null;
  memberUsername: string | null;
  memberFirstName: string | null;
  memberLastName: string | null;
  /** Times they actually took the drop — «Всего раз». */
  collectedCount: number;
  /** ISO 8601, or null when they never have. */
  lastCollectedAt: string | null;
  /** Times their turn came up and they were not there. */
  skippedCount: number;
  lastSkippedAt: string | null;
}

export interface LootHistoryEntry {
  id: number;
  characterId: number;
  characterName: string;
  kind: 'collected' | 'skipped';
  assignedAt: string;
  note: string | null;
  recordedByUserId: number | null;
}

export interface LootQueueResponse {
  queue: LootQueueEntry[];
  history: LootHistoryEntry[];
}

/**
 * What came of a loot write — whether the bot said anything, and if not, why
 * (DECISION-33, narrowed by DECISION-43).
 *
 * ⚠️ `posted: false` IS NOT AN ERROR, in any of its forms, and the UI must not
 * render it as one. Four of the five reasons are the feature declining to make
 * noise — the queue is empty, nothing moved, the action never speaks, or the
 * action was a reorder — and only `bot_disabled` describes configuration. A
 * screen that flashes red when the bot correctly stays quiet trains an admin to
 * ignore it.
 *
 * ⚠️ THREE OF THE SIX LOOT ACTIONS NOW NEVER REACH THE BOT AT ALL. Since
 * DECISION-43, moving the queue — to the front, to the back, or by dragging — is
 * a PLAN and is silent; the group hears about the rotation from «Собрал лут»,
 * which reports something that happened, or from «Объявить очередь», which is a
 * person deciding to say so. Those three paths construct their outcome directly
 * and make no call, so they also work when the provider is down.
 */
export type LootAnnouncementOutcome =
  | { posted: true }
  | {
      posted: false;
      reason:
        /** Nobody is in the rotation yet. */
        | 'empty_queue'
        /**
         * The queue did not move — they were already at that end, or a drag
         * landed where it started. Distinct from every other reason here in
         * that it is a fact about the QUEUE rather than about the bot.
         */
        | 'unchanged'
        /**
         * This action never announces, by design.
         *
         * ⚠️ NOT THE SAME AS `unchanged`, and folding them together would make
         * the UI lie. «Не явился» DOES reorder the queue; the silence is a
         * decision the owner made about which changes are worth a message, not
         * an observation that nothing happened. An admin who marks a no-show and
         * is told "ничего не изменилось" would reasonably conclude the button
         * was broken and press it again.
         */
        | 'not_announced'
        /**
         * A reorder, which is silent by design (DECISION-43).
         *
         * ⚠️ A SEPARATE REASON FROM `not_announced` RATHER THAN A SHARED ONE,
         * for the same argument written one entry up: the sentences a reader
         * needs are different. «Не явился» has to say the absence stays between
         * the admin and the log; a reorder has to say the new order was SAVED,
         * because the only other evidence of that is a list that already looked
         * right the instant the row was dropped. One reason cannot carry both,
         * and the tone mapping in the client is derived from the reason alone.
         */
        | 'reorder_not_announced'
        /** TELEGRAM_BOT_MODE=disabled — the state this repo ships in. */
        | 'bot_disabled'
        /** The provider could not be reached. */
        | 'send_failed';
    };

/** Every loot write answers with the new queue AND what the bot did about it. */
export interface LootMutationResponse extends LootQueueResponse {
  announcement: LootAnnouncementOutcome;
}

/**
 * A person this app has seen, for the "who is this about" picker (DECISION-34).
 *
 * Not a list of current group members — `members` is a profile cache, so
 * someone who has left is still here. See `listMemberIdentities`.
 */
export interface MemberIdentity {
  telegramUserId: number;
  username: string | null;
  firstName: string | null;
  lastName: string | null;
  characterName: string | null;
}

export interface MembersResponse {
  members: MemberIdentity[];
}

/* -------------------------------------------------------------------------
 * Naming (DECISION-36)
 * ---------------------------------------------------------------------- */

/**
 * Fold a name to the form comparisons happen in.
 *
 * ⚠️ IT LIVES IN THE CONTRACT PACKAGE BECAUSE THREE LAYERS MUST AGREE ON IT.
 * `character_aliases.folded` is a stored column with a UNIQUE index on it —
 * that index is what refuses a second «Костян» — and the matcher in
 * `bot/subject.ts` looks a name up against the same column. If the writer and
 * the reader folded differently, an alias would be storable and unfindable,
 * with nothing to say so. It was in `bot/subject.ts` until DECISION-36 needed
 * the database to write the same string.
 *
 * ё→е because the two spellings are one name to everyone who types them, and
 * NFC first for the same reason `bot/memory.ts` does it: NFD-plus-strip would
 * decompose й as well and turn «Андрей» into «Андреи».
 *
 * ⚠️ DELIBERATELY NOT THE TOKENISER IN `bot/memory.ts`. That one stems to four
 * characters to decide whether two FACTS are the same fact, where over-folding
 * costs a duplicate row. This is matching a PERSON, where over-folding costs
 * the wrong person — «Влад» and «Влада» are one name, «Кабан» and «Кабанов»
 * probably are, and four-character stemming would also merge «Мелкор» with
 * «Мелхиор». Different jobs, different tolerances, so different functions.
 *
 * Pure and dependency-free, like everything else here — see the header.
 */
export function foldName(value: string): string {
  return value
    .normalize('NFC')
    .toLowerCase()
    .replace(/ё/g, 'е')
    .replace(/^@+/, '')
    .replace(/\s+/gu, ' ')
    .trim();
}

/**
 * Longest an alias may be.
 *
 * An alias is a word or two — «Костян», «дядя Витя». The cap is not about
 * storage: an alias is matched against what somebody typed in a chat message,
 * and a 200-character one could never be matched by anything, so accepting it
 * would create a row that silently does nothing.
 */
export const MAX_ALIAS_CHARS = 40;

/**
 * One admin-typed name the group actually uses for somebody (DECISION-36).
 *
 * ⚠️ `folded` IS NOT SENT TO THE BROWSER, and its absence here is deliberate.
 * It is a matching key, and a client that could see it would be able to
 * reimplement the collision rule — badly, and out of step with the unique index
 * that actually enforces it. The page asks the server and renders the answer.
 */
export interface CharacterAlias {
  id: number;
  /** As the admin typed it. Rendered verbatim; matching folds it server-side. */
  alias: string;
}

/**
 * A person in the clan, as Админка → Состав lists them (DECISION-36).
 *
 * ⚠️ A PERSON IS A CHARACTER (DECISION-38). Two kinds, and the difference is
 * whether anybody owns it:
 *
 *   characterId + memberId  the ordinary case — a member with a character
 *   characterId only        a player with no Telegram (DECISION-33)
 *
 * Somebody who has logged in and never made a character is NOT here. That was
 * the third kind for a day, and against the real clan it produced a row with no
 * character, no attributes, no karma and no aliases — they hang off the
 * character — under a paragraph explaining why nothing could be done with it.
 * Making a character is two taps and is the same act that puts somebody on the
 * roster and in the loot queue.
 */
export interface PersonSummary {
  characterId: number;
  characterName: string;
  /** Telegram user id. Null for an admin-created character with no owner. */
  memberId: number | null;
  username: string | null;
  firstName: string | null;
  lastName: string | null;
  aliases: CharacterAlias[];
}

export interface PeopleResponse {
  people: PersonSummary[];
}

/* -------------------------------------------------------------------------
 * Karma (DECISION-37)
 * ---------------------------------------------------------------------- */

/**
 * Points one member may give one character in a Moscow day.
 *
 * ⚠️ NAMED HERE BECAUSE THE CLIENT SAYS IT OUT LOUD. The rule is enforced
 * server-side and always will be — the number in the browser decides only
 * whether a heart is offered and what the refusal reads like. A hardcoded «2» in
 * a Russian sentence on the home page is how the two get to disagree.
 */
export const KARMA_POINTS_PER_DAY = 2;

/** Longest thing somebody may write on a karma point. */
export const MAX_KARMA_MESSAGE_CHARS = 280;

/**
 * Why a karma grant was refused.
 *
 * ⚠️ THREE REASONS, KEPT APART ALL THE WAY TO THE SENTENCE THE MEMBER READS.
 * «Себе нельзя», «на сегодня всё» and «персонажа больше нет» are different
 * facts, and only the middle one is worth coming back tomorrow for. A single
 * "не удалось" trains somebody to press the button again.
 */
export type KarmaRefusalCode = 'self' | 'spent' | 'unknown_character';

/** One point somebody gave, as the history modal shows it. */
export interface KarmaEntry {
  id: number;
  /**
   * Who gave it, as a label, or null when this app has no name for them.
   *
   * Null is a real state rather than missing data: karma can be given from the
   * group chat by somebody who has never opened the web app, so there may be no
   * profile and no recorded name — only a Telegram id, which is never shown
   * because nobody recognises one.
   */
  fromName: string | null;
  fromUsername: string | null;
  /** Why, in their words. Null when they gave a point without saying anything. */
  message: string | null;
  /** Which surface it came from; `bot` is the one that went through name resolution. */
  source: 'web' | 'bot';
  grantedAt: string;
}

/** GET /api/karma/:characterId */
export interface KarmaResponse {
  characterId: number;
  characterName: string;
  total: number;
  /** How many the VIEWER may still give this character today, 0..KARMA_POINTS_PER_DAY. */
  remainingToday: number;
  /**
   * Set when the viewer may not give at all, whatever the count says.
   *
   * ⚠️ SEPARATE FROM `remainingToday` REACHING ZERO. Your own character reports
   * `remainingToday: 0` too, and «на сегодня всё» would be a lie about it — the
   * answer there is never, not "tomorrow".
   */
  refusal: KarmaRefusalCode | null;
  entries: KarmaEntry[];
}

/** POST /api/karma */
export interface KarmaGrantResponse {
  characterId: number;
  total: number;
  remainingToday: number;
}

/**
 * The attribute key the home page reads as a character's level.
 *
 * A convention, not a schema: an admin creates «Уровень» with this key in
 * Админка → Атрибуты, and the roster starts sorting by it. Named here rather
 * than typed as a literal in the page so the server, the client and anyone
 * seeding the catalogue are reading the same string.
 */
export const LEVEL_ATTRIBUTE_KEY = 'level';

/**
 * The attribute key the home page reads as a character's role.
 *
 * The same convention as `LEVEL_ATTRIBUTE_KEY` and subject to the same rule: a
 * deployment with no attribute under this key simply does not get the column.
 * Its VALUE is rendered as text whatever the attribute's type — a role is a
 * label, and «Сорцерер» reads the same whether it was stored as free text or
 * picked from a list.
 */
export const ROLE_ATTRIBUTE_KEY = 'role';

/**
 * A stored attribute value read as a LEVEL, or null.
 *
 * ⚠️ IT LIVES HERE BECAUSE TWO SIDES READ THE SAME ATTRIBUTE NOW. The home page
 * has always coerced it for the roster column; since DECISION-38 the bot reads
 * it too, to answer «какой у Лёхи уровень». A second hand-written coercion is
 * how the two come to disagree about a level somebody typed as text — and the
 * disagreement would be between what the screen shows and what the bot says out
 * loud in the group.
 *
 * Strict, unlike `coerceRole`: a level is a NUMBER, and anything that will not
 * become one is absent rather than guessed at. The catalogue is admin-seeded
 * (DECISION-9), so a deployment may have no such attribute at all, or one
 * somebody defined as text — both ordinary states, neither an error.
 */
export function coerceLevel(raw: AttributeValue['value']): number | null {
  if (typeof raw === 'number') return Number.isFinite(raw) ? raw : null;
  if (typeof raw === 'string' && raw.trim() !== '') {
    const parsed = Number(raw);
    return Number.isFinite(parsed) ? parsed : null;
  }
  return null;
}

/**
 * ...and as a ROLE.
 *
 * Deliberately type-agnostic where `coerceLevel` is strict: a role is a LABEL,
 * so free text, a single select and even a multi-select all render the same
 * way. An array is JOINED rather than stringified, because `String(['Танк',
 * 'Хил'])` is «Танк,Хил» with no space.
 */
export function coerceRole(raw: AttributeValue['value']): string | null {
  if (Array.isArray(raw)) {
    const parts = raw.filter((part) => typeof part === 'string' && part.trim() !== '');
    return parts.length === 0 ? null : parts.join(', ');
  }
  if (typeof raw === 'string') return raw.trim() === '' ? null : raw.trim();
  if (typeof raw === 'number') return String(raw);
  return null;
}

/* -------------------------------------------------------------------------
 * Chat activity (GET /api/activity)
 * ---------------------------------------------------------------------- */

/**
 * The windows «Кто пишет в чате» can be read over.
 *
 * A closed list rather than a free-form range: the card is a glance, not a
 * report, and every extra window is another set of numbers somebody has to
 * decide whether to believe.
 */
export const ACTIVITY_PERIODS = ['all', 'year', 'month', 'week'] as const;

export type ActivityPeriod = (typeof ACTIVITY_PERIODS)[number];

export function isActivityPeriod(value: unknown): value is ActivityPeriod {
  return typeof value === 'string' && (ACTIVITY_PERIODS as readonly string[]).includes(value);
}

/**
 * One person's share of everything said in the window.
 *
 * ⚠️ THERE IS NO COUNT ON THIS TYPE, AND ITS ABSENCE IS THE FEATURE. The
 * question is a relative one — who is around and who is not — and a raw
 * per-person total is a different, worse thing to put on a page every member
 * can see: it invites «у меня 412, а у тебя 38» in a group of friends. Not
 * putting it on the wire means no client can render it even by accident, which
 * is a stronger guarantee than agreeing not to.
 */
export interface ActivityShareEntry {
  telegramUserId: number;
  /** As Telegram last sent it. Verbatim — never transliterated (PRD §7). */
  firstName: string | null;
  username: string | null;
  /** 0..1 of every countable message in the window. */
  share: number;
}

export interface ActivityResponse {
  period: ActivityPeriod;
  entries: ActivityShareEntry[];
  /**
   * The first message the window actually contains, ISO 8601, or null when it
   * contains none.
   *
   * ⚠️ THIS IS WHAT MAKES «ВСЁ ВРЕМЯ» HONEST. The message log starts when the
   * bot was switched on, not when the group was created, so "all time" is a
   * claim this app cannot make — it can only say what it has, and the card
   * renders this date instead of the words.
   */
  since: string | null;
  /**
   * Whether there are enough messages for a percentage to mean anything.
   *
   * ⚠️ A BOOLEAN RATHER THAN THE TOTAL, for the same reason the entries carry
   * no count: sending the denominator would let any caller multiply it back out
   * and recover every per-person total.
   */
  enough: boolean;
}

/**
 * Split every activity row into a name and a handle.
 *
 * ⚠️ IT LIVES IN THE CONTRACT BECAUSE TWO SURFACES MUST AGREE. The home-page
 * card and the end-of-month announcement name the same people from the same
 * data; if one showed a handle and the other did not, the message in the group
 * would credit «Лёха» while the card beside it showed two of them, and nobody
 * could tell which one won. One function, both callers.
 *
 * ⚠️ THE HANDLE IS RETURNED SEPARATELY RATHER THAN CONCATENATED INTO THE LABEL,
 * because the two callers set it differently: the card renders it as a second,
 * muted span on the same line, and the announcement puts it in brackets inside
 * one line of plain text. A pre-joined string would force one of them to take
 * the other's punctuation apart again.
 *
 * ⚠️ AND IT IS SHOWN FOR EVERYBODY, NOT ONLY FOR DUPLICATES (owner's call,
 * 2026-08-13). The first version attached it only when two people shared a
 * first name, which was clever and wrong in practice: whether your row carries
 * a handle then depends on who ELSE spoke this month, so the same person reads
 * differently in «Месяц» and «Год» — and a reader cannot know that the absence
 * of a handle is meaningful rather than missing. Always showing it makes every
 * row say the same kind of thing.
 *
 * ⚠️ `handle` IS NULL WHEN IT WOULD ONLY REPEAT THE LABEL. Telegram does not
 * require a username, so somebody with no first name falls back to their
 * @handle as the label — and «@ghost @ghost» is not a disambiguation.
 *
 * A bare numeric id is never shown either. Nobody recognises one.
 */
export function nameActivityShares<T extends ActivityShareEntry>(
  entries: readonly T[],
): (T & { label: string; handle: string | null })[] {
  return entries.map((entry) => {
    const name = entry.firstName?.trim();
    const username = entry.username?.trim();
    const handle = username ? `@${username}` : null;

    if (name) return { ...entry, label: name, handle };
    // The handle IS the label here, so returning it twice would read as noise.
    if (handle) return { ...entry, label: handle, handle: null };
    return { ...entry, label: 'участник без имени', handle: null };
  });
}
